import { Alert, Badge, Button, Card, CardHeader, Switch } from '@dnx/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { FileSignature } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api.js';
import { meQuery } from '../lib/session.js';
import { patientSignaturesQuery, SIG_STATUS, SignatureViewer, when } from './shared.js';

/** Aba Documentos da ficha: TCLE (equipe clínica) e orçamentos assinados. */
export function PatientDocumentsTab({ patientId }: { patientId: string }) {
  const { data: me } = useSuspenseQuery(meQuery);
  const canWrite = me.permissions.includes('clinical.record.write');
  const qc = useQueryClient();
  const { data } = useQuery(patientSignaturesQuery(patientId));
  const [notify, setNotify] = useState(true);
  const [open, setOpen] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: () => api<{ notified: boolean }>(`/patients/${patientId}/signatures/tcle`, { method: 'POST', body: { notify } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['signatures'] }),
  });
  const pendingTcle = data?.some((d) => d.kind === 'tcle' && d.status === 'pending');
  return (
    <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
      <Card>
        <CardHeader title="Documentos" description="Enviados ao paciente para assinar na área do paciente. O texto fica congelado no envio." />
        <ul className="divide-y divide-border">
          {data?.map((d) => (
            <li key={d.id}>
              <button type="button" onClick={() => setOpen(d.id)} className="flex w-full items-center gap-3 px-5 py-3.5 text-left hover:bg-surface-muted">
                <FileSignature className="size-4 shrink-0 text-fg-subtle" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium text-fg">{d.title}</span>
                  <span className="text-[12.5px] text-fg-muted">
                    {d.status === 'signed' && d.signedAt ? `assinado ${when(d.signedAt)} por ${d.signerName}` : `enviado ${when(d.createdAt)}${d.notifiedAt ? ' · avisado no WhatsApp' : ''}`}
                  </span>
                </span>
                <Badge tone={SIG_STATUS[d.status][1]}>{SIG_STATUS[d.status][0]}</Badge>
              </button>
            </li>
          ))}
          {data && !data.length && <li className="px-5 py-6 text-sm text-fg-muted">Nenhum documento enviado.</li>}
        </ul>
      </Card>
      {canWrite && (
        <Card className="h-fit">
          <CardHeader title="Termo de consentimento (TCLE)" description="Preenchido com os dados do paciente e da avaliação assinada. O modelo fica em Configurações › Documentos." />
          <div className="grid gap-4 p-5">
            <Switch checked={notify} onChange={setNotify} label="Avisar pelo WhatsApp" description="Manda o link para ler e assinar." />
            {pendingTcle && <Alert tone="info">Já há um TCLE aguardando assinatura. Enviar de novo cria outro pedido.</Alert>}
            {send.error && <Alert tone="danger">{send.error.message}</Alert>}
            {send.isSuccess && <Alert tone="success">TCLE enviado{send.data.notified ? ' e avisado no WhatsApp' : ''}.</Alert>}
            <Button onClick={() => send.mutate()} loading={send.isPending}><FileSignature className="size-4" />Enviar TCLE para assinatura</Button>
          </div>
        </Card>
      )}
      {open && <SignatureViewer id={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
